import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { listCrmLeads, moveLeadToStage } from "../../../../services/platform_lead_service";
import { listUsers } from "../../../../services/platform_admin_service";
import { SALES_STAGES } from "../constants";

const initialColumns = () => {
    const cols = {};
    SALES_STAGES.forEach(stage => { cols[stage.key] = []; });
    return cols;
};

const ACTIVE_STAGES = ["new_lead", "contacted", "demo_scheduled", "demo_done", "trial_active", "proposal_sent"];
const DAY_MS = 1000 * 60 * 60 * 24;

export const getDaysSinceContact = (lead) => {
    const ref = lead.salesData?.lastContactAt || lead.salesData?.stagedAt || lead.createdAt;
    if (!ref) return null;
    const time = new Date(ref).getTime();
    if (Number.isNaN(time)) return null;
    return Math.max(0, Math.floor((Date.now() - time) / DAY_MS));
};

export const leadNeedsAction = (lead) => {
    if (lead.salesData?.isArchived) return false;
    if (!ACTIVE_STAGES.includes(lead.salesData?.salesStage)) return false;
    const days = getDaysSinceContact(lead);
    return days !== null && days >= 3;
};

const usePlatformPipeline = () => {
    const [allLeads, setAllLeads] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [needsActionOnly, setNeedsActionOnly] = useState(false);
    const [filters, setFilters] = useState({
        consultantId: "",
        stage: "",
        temperature: "",
        source: "",
        search: ""
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sempre busca todos os leads (incluindo arquivados); filtragem é client-side
    const fetchLeads = useCallback(async (currentFilters = {}) => {
        try {
            setLoading(true);
            setError(null);
            const result = await listCrmLeads({
                page: 1,
                pageSize: 200,
                filters: { ...currentFilters, includeArchived: true }
            });
            setAllLeads(result.items || []);
        } catch (err) {
            console.error("[usePlatformPipeline] Erro ao buscar leads:", err);
            setError(err.message || "Erro ao carregar leads.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchConsultantsList = useCallback(async () => {
        try {
            const result = await listUsers({ page: 1, pageSize: 100 });
            setConsultants(result.items || []);
        } catch (err) {
            console.error("[usePlatformPipeline] Erro ao carregar consultores:", err);
        }
    }, []);

    useEffect(() => { fetchLeads(filters); }, [fetchLeads, filters]);
    useEffect(() => { fetchConsultantsList(); }, [fetchConsultantsList]);

    // Agrupa leads nas colunas, filtrando arquivados conforme showArchived
    const { leads, columns, archivedCount, kpis } = useMemo(() => {
        const cols = initialColumns();
        const visible = [];
        let archived = 0;

        // KPIs do funil (sempre sobre o conjunto completo, sem filtro visual)
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        let activeCount = 0;
        let needsActionCount = 0;
        let demosScheduled = 0;
        let trialsActive = 0;
        let wonMonth = 0;
        let wonTotal = 0;
        let closedTotal = 0;

        allLeads.forEach(lead => {
            const sd = lead.salesData || {};
            const stage = sd.salesStage || "new_lead";

            if (!sd.isArchived) {
                if (ACTIVE_STAGES.includes(stage)) activeCount++;
                if (leadNeedsAction(lead)) needsActionCount++;
                if (stage === "demo_scheduled") demosScheduled++;
                if (stage === "trial_active") trialsActive++;
            }
            if (stage === "won") {
                wonTotal++;
                closedTotal++;
                if (sd.wonAt && new Date(sd.wonAt).getTime() >= monthStart) wonMonth++;
            }
            if (stage === "lost" || stage === "cold") closedTotal++;

            if (sd.isArchived) {
                archived++;
                if (!showArchived) return;
            }
            if (needsActionOnly && !leadNeedsAction(lead)) return;
            visible.push(lead);
            if (cols[stage]) cols[stage].push(lead);
            else cols.new_lead.push(lead);
        });

        const conversionRate = closedTotal > 0 ? Math.round((wonTotal / closedTotal) * 100) : 0;

        return {
            leads: visible,
            columns: cols,
            archivedCount: archived,
            kpis: {
                activeCount,
                needsActionCount,
                demosScheduled,
                trialsActive,
                wonMonth,
                conversionRate,
            },
        };
    }, [allLeads, showArchived, needsActionOnly]);

    const applyFilters = (newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    const toggleArchive = useCallback(() => setShowArchived(prev => !prev), []);

    const handleDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const leadId = draggableId;
        const sourceStage = source.droppableId;
        const destStage = destination.droppableId;

        // Cópia profunda para rollback
        const prevAllLeads = [...allLeads];

        // Atualização otimista: altera salesStage no allLeads
        setAllLeads(prev => prev.map(l =>
            l.id === leadId
                ? { ...l, salesData: { ...l.salesData, salesStage: destStage, stagedAt: new Date().toISOString() } }
                : l
        ));

        toast.info("Movendo lead...");

        try {
            await moveLeadToStage({
                idTenant: leadId,
                targetStage: destStage,
                notes: `Movido de '${sourceStage}' para '${destStage}' via painel Kanban.`
            });
            toast.success("Lead movido com sucesso!");
        } catch (err) {
            console.error("[usePlatformPipeline] Falha ao mover lead:", err);
            toast.error(err.message || "Erro ao mover lead.");
            setAllLeads(prevAllLeads);
        }
    };

    // Fila do Dia: leads ativos sem contato há 1+ dia, priorizados por
    // urgência (dias sem contato) e potencial (lead score)
    const actionQueue = useMemo(() => {
        return allLeads
            .filter(l => !l.salesData?.isArchived && ACTIVE_STAGES.includes(l.salesData?.salesStage))
            .map(l => ({
                lead: l,
                days: getDaysSinceContact(l) ?? 0,
                score: l.salesData?.leadScore ?? 0,
            }))
            .filter(x => x.days >= 1)
            .sort((a, b) => (b.days * 10 + b.score) - (a.days * 10 + a.score))
            .slice(0, 8)
            .map(x => x.lead);
    }, [allLeads]);

    const toggleNeedsAction = useCallback(() => setNeedsActionOnly(prev => !prev), []);

    return {
        leads,
        columns,
        consultants,
        showArchived,
        archivedCount,
        toggleArchive,
        kpis,
        actionQueue,
        needsActionOnly,
        toggleNeedsAction,
        filters,
        loading,
        error,
        applyFilters,
        handleDragEnd,
        refetch: () => fetchLeads(filters)
    };
};

export default usePlatformPipeline;
